import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { OddsCurveChart } from "@/components/rikkus/OddsCurveChart";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";
import { fmtOdds } from "@/lib/odds";
import { Skeleton } from "@/components/ui/skeleton";

export default function RaceViewPage() {
    const { raceId } = useParams();
    const nav = useNavigate();
    const [race, setRace] = useState(null);
    const [entries, setEntries] = useState([]);
    const [snapsNyra, setSnapsNyra] = useState([]);
    const [snapsBet, setSnapsBet] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.all([
            api.get(`/races/${raceId}`),
            api.get(`/races/${raceId}/entries`),
            api.get(`/races/${raceId}/snapshots?source=NYRA_RDL`),
            api.get(`/races/${raceId}/snapshots?source=BET365`),
            api.get(`/races/${raceId}/results`),
        ]).then(([r1, r2, r3, r4, r5]) => {
            if (!alive) return;
            setRace(r1.data);
            setEntries(r2.data || []);
            setSnapsNyra(r3.data || []);
            setSnapsBet(r4.data || []);
            setResults(r5.data || []);
        }).catch(() => {}).finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [raceId]);

    const hasFullFinish = results.length > 0 && results.every(r => r.finish_position != null);

    return (
        <>
            <Topbar title={`Carrera R${race?.race_number ?? "?"} — ${race?.track_id ?? ""}`} subtitle={race?.race_date} />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                <div>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => nav(-1)}>
                        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Volver
                    </Button>
                </div>
                {loading ? <Skeleton className="h-64 w-full" /> : (
                    <>
                        <Card>
                            <CardHeader className="pb-3"><CardTitle className="text-sm">Header</CardTitle></CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                                    <MetaTile label="race_id" value={race?.race_id} />
                                    <MetaTile label="Post scheduled (UTC)" value={race?.scheduled_post_at} />
                                    <MetaTile label="Actual off (UTC)" value={race?.actual_off_at} />
                                    <MetaTile label="Surface" value={race?.surface} />
                                    <MetaTile label="Distance (f)" value={race?.distance_furlongs} />
                                    <MetaTile label="Race type" value={race?.race_type} />
                                    <MetaTile label="Purse USD" value={race?.purse_usd} />
                                    <MetaTile label="Field carded / final" value={`${race?.field_size_carded ?? "?"} / ${race?.field_size_final ?? "?"}`} />
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm">Curva de odds — NYRA vs BET365</CardTitle>
                                <div className="flex items-center gap-3 text-[11px] font-mono">
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-nyra" /> NYRA_RDL</span>
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-bet365" style={{borderTop:"2px dashed hsl(var(--bet365))", background:"transparent"}} /> BET365</span>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <OddsCurveChart snapshotsNyra={snapsNyra} snapshotsBet365={snapsBet} />
                            </CardContent>
                        </Card>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Card>
                                <CardHeader className="pb-3"><CardTitle className="text-sm">Programa</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="rounded-md border border-border overflow-hidden">
                                        <Table data-testid="race-entries-table">
                                            <TableHeader>
                                                <TableRow className="bg-muted/40 hover:bg-muted/40">
                                                    <TableHead className="h-8 text-[11px]">#</TableHead>
                                                    <TableHead className="h-8 text-[11px]">Caballo</TableHead>
                                                    <TableHead className="h-8 text-[11px]">Jinete</TableHead>
                                                    <TableHead className="h-8 text-[11px]">Entrenador</TableHead>
                                                    <TableHead className="h-8 text-[11px] text-right">ML</TableHead>
                                                    <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {entries.map(e => (
                                                    <TableRow key={e.entry_id} className="h-8">
                                                        <TableCell className="font-mono text-xs">{e.program_number}</TableCell>
                                                        <TableCell className="text-xs">{e.horse_name}</TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">{e.jockey || "—"}</TableCell>
                                                        <TableCell className="text-xs text-muted-foreground">{e.trainer || "—"}</TableCell>
                                                        <TableCell className="text-right font-mono text-xs">{fmtOdds(e.morning_line_odds)}</TableCell>
                                                        <TableCell>{e.scratched ? <StatusBadge kind="miss">SCR</StatusBadge> : (e.also_eligible ? <StatusBadge kind="pending">A/E</StatusBadge> : <StatusBadge kind="verified">OK</StatusBadge>)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                    <CardTitle className="text-sm">Resultados</CardTitle>
                                    {results.length > 0 && !hasFullFinish && <StatusBadge kind="partial">PARCIAL (4º en adelante)</StatusBadge>}
                                </CardHeader>
                                <CardContent>
                                    <div className="rounded-md border border-border overflow-hidden">
                                        <Table data-testid="race-results-table">
                                            <TableHeader>
                                                <TableRow className="bg-muted/40 hover:bg-muted/40">
                                                    <TableHead className="h-8 text-[11px]">Pos</TableHead>
                                                    <TableHead className="h-8 text-[11px]">#</TableHead>
                                                    <TableHead className="h-8 text-[11px]">Caballo</TableHead>
                                                    <TableHead className="h-8 text-[11px] text-right">Final odds</TableHead>
                                                    <TableHead className="h-8 text-[11px] text-right">Win $2</TableHead>
                                                    <TableHead className="h-8 text-[11px] text-right">Place $2</TableHead>
                                                    <TableHead className="h-8 text-[11px] text-right">Show $2</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {results.length === 0 && (
                                                    <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">Sin resultados oficiales.</TableCell></TableRow>
                                                )}
                                                {results.map(r => (
                                                    <TableRow key={r.result_id} className="h-8">
                                                        <TableCell className="font-mono text-xs">{r.finish_position ?? "—"}</TableCell>
                                                        <TableCell className="font-mono text-xs">{r.program_number}</TableCell>
                                                        <TableCell className="text-xs">{r.horse_name || "—"}</TableCell>
                                                        <TableCell className="text-right font-mono text-xs">{fmtOdds(r.final_win_odds)}</TableCell>
                                                        <TableCell className="text-right font-mono text-xs">{fmtOdds(r.win_payout_per_2)}</TableCell>
                                                        <TableCell className="text-right font-mono text-xs">{fmtOdds(r.place_payout_per_2)}</TableCell>
                                                        <TableCell className="text-right font-mono text-xs">{fmtOdds(r.show_payout_per_2)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

function MetaTile({ label, value }) {
    const isTime = typeof value === "string" && /\d{4}-\d{2}-\d{2}T/.test(value);
    return (
        <div className="border border-border rounded-md p-2 bg-card">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            {isTime
                ? <div className="mt-0.5"><TimestampET value={value} /></div>
                : <div className="mt-0.5 font-mono text-xs">{value ?? "—"}</div>}
        </div>
    );
}
